import { useSearch } from 'wouter';

export interface MarketingModeState {
  isDemo: boolean;
  language: 'sk' | 'en';
  tenantId: string | null;
}

/**
 * Hook for detecting and managing marketing demo mode
 */
export function useMarketingMode(): MarketingModeState {
  const search = useSearch();
  const searchParams = new URLSearchParams(search);
  
  const isDemo = searchParams.get('demo') === '1' || import.meta.env.VITE_APP_MODE === 'marketing';
  const language = (searchParams.get('lang') as 'sk' | 'en') || 'sk';
  
  const tenantId = isDemo ? `demo-${language}` : null;
  
  return {
    isDemo,
    language,
    tenantId
  };
}

/**
 * Generate demo URL with proper language and demo parameters
 */
export function createDemoUrl(path: string, lang: 'sk' | 'en' = 'sk', additionalParams: Record<string, string> = {}): string {
  const params = new URLSearchParams({
    demo: '1',
    lang,
    ...additionalParams
  });
  
  return `${path}?${params.toString()}`;
}

/**
 * Format currency based on demo language
 */
export function formatDemoCurrency(amount: number, lang: 'sk' | 'en'): string {
  if (lang === 'sk') {
    return new Intl.NumberFormat('sk-SK', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(amount);
  } else {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  }
}

/**
 * Format date based on demo language
 */
export function formatDemoDate(date: Date, lang: 'sk' | 'en'): string {
  if (lang === 'sk') {
    return new Intl.DateTimeFormat('sk-SK', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  } else {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  }
}